import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Toolbar,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  AttachFile,
  Send,
  CheckCircle,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  ArrowBack,
  Add,
  Refresh,
  Help,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { ticketsApi } from '../api/ticketsApi';
import { TicketCreate, Ticket } from '../types/ticket';
import TicketingUserGuide from '../components/TicketingUserGuide';

const TicketingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getUser } = useAuthStore();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Tab indices: 0=Submit, 1=My Tickets (if authenticated), 2=All Tickets
  const getTabIndex = (tabName: string) => {
    if (!isAuthenticated) {
      return tabName === 'Submit Ticket' ? 0 : 1; // Only 2 tabs for non-authenticated
    }
    switch (tabName) {
      case 'Submit Ticket': return 0;
      case 'My Tickets': return 1;
      case 'All Tickets': return 2;
      default: return 0;
    }
  };
  
  const [formData, setFormData] = useState<TicketCreate>({
    user_email: user?.email || '',
    user_name: user?.full_name || '',
    subject: '',
    description: '',
    is_logged_in_user: !!isAuthenticated,
  });
  
  const editorRef = useRef<HTMLDivElement>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submittedTicket, setSubmittedTicket] = useState<Ticket | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [updateForm, setUpdateForm] = useState({
    status: 'open' as 'open' | 'in_progress' | 'closed',
    response_notes: '',
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    subject: '',
    description: '',
  });
  const [editLoading, setEditLoading] = useState(false);
  const editEditorRef = useRef<HTMLDivElement>(null);
  const [editAttachments, setEditAttachments] = useState<File[]>([]);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitAttachments, setSubmitAttachments] = useState<File[]>([]);
  const submitEditorRef = useRef<HTMLDivElement>(null);
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [userCaptchaInput, setUserCaptchaInput] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTicket, setViewTicket] = useState<Ticket | null>(null);
  const [guideDialogOpen, setGuideDialogOpen] = useState(false);

  // Load Cloudflare Turnstile script for non-logged-in users (disabled for now)
  useEffect(() => {
    // Turnstile is disabled for now until site key is configured
    return;
    
    if (!isAuthenticated) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      
      // Make turnstile available globally
      (window as any).turnstileCallback = (token: string) => {
        setTurnstileToken(token);
      };
      
      return () => {
        // Cleanup script
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }
  }, [isAuthenticated]);

  // Update form data when user becomes available
  useEffect(() => {
    if (isAuthenticated && user) {
      const userEmail = user.email || '';
      const userName = user.full_name || user.email || ''; // Fallback to email if no full_name
      
      setFormData(prev => ({
        ...prev,
        user_email: userEmail,
        user_name: userName,
        is_logged_in_user: true,
      }));
    } else if (!isAuthenticated) {
      // Reset form data for non-authenticated users
      setFormData(prev => ({
        ...prev,
        user_email: '',
        user_name: '',
        is_logged_in_user: false,
      }));
    }
  }, [isAuthenticated, user]);

  // Fetch user data on component mount if authenticated but no user data
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (isAuthenticated && !user && token) {
      getUser().catch(error => {
        console.error('Failed to fetch user data:', error);
        // If getUser fails, we might need to update authentication state
        // This can happen if the token is expired
      });
    }
  }, [isAuthenticated, user, getUser]);

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setEditAttachments(prev => [...prev, ...files]);
  };

  const handleEditRemoveAttachment = (index: number) => {
    setEditAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditFormatText = (command: string) => {
    if (!editEditorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    
    switch (command) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      case 'italic':
        document.execCommand('italic', false);
        break;
      case 'underline':
        document.execCommand('underline', false);
        break;
      case 'insertUnorderedList':
        document.execCommand('insertUnorderedList', false);
        break;
      case 'insertOrderedList':
        document.execCommand('insertOrderedList', false);
        break;
      case 'justifyLeft':
        document.execCommand('justifyLeft', false);
        break;
      case 'justifyCenter':
        document.execCommand('justifyCenter', false);
        break;
      case 'justifyRight':
        document.execCommand('justifyRight', false);
        break;
    }
    
    editEditorRef.current.focus();
  };

  const handleSubmitDialogOpen = () => {
    setSubmitDialogOpen(true);
    setError('');
    // Reset form data
    setFormData({
      user_email: user?.email || '',
      user_name: user?.full_name || user?.email || '',
      subject: '',
      description: '',
      is_logged_in_user: !!isAuthenticated,
    });
    setSubmitAttachments([]);
    
    // Generate CAPTCHA for non-authenticated users
    if (!isAuthenticated) {
      generateCaptcha();
    }
    
    // Clear editor content after dialog opens
    setTimeout(() => {
      if (submitEditorRef.current) {
        submitEditorRef.current.innerText = '';
      }
    }, 100);
  };

  const handleSubmitDialogClose = () => {
    setSubmitDialogOpen(false);
    setError('');
    setSubmitAttachments([]);
  };

  const handleFormatSubmitText = (command: string) => {
    if (!submitEditorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    
    switch (command) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      case 'italic':
        document.execCommand('italic', false);
        break;
      case 'underline':
        document.execCommand('underline', false);
        break;
      case 'insertUnorderedList':
        document.execCommand('insertUnorderedList', false);
        break;
      case 'insertOrderedList':
        document.execCommand('insertOrderedList', false);
        break;
      case 'justifyLeft':
        document.execCommand('justifyLeft', false);
        break;
      case 'justifyCenter':
        document.execCommand('justifyCenter', false);
        break;
      case 'justifyRight':
        document.execCommand('justifyRight', false);
        break;
    }
    
    submitEditorRef.current.focus();
  };

  const handleSubmitAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSubmitAttachments(prev => [...prev, ...files]);
  };

  const handleSubmitRemoveAttachment = (index: number) => {
    setSubmitAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleOpenViewDialog = (ticket: Ticket) => {
    setViewTicket(ticket);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewTicket(null);
  };

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operations = ['+', '-', '*'];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    
    let answer;
    let question;
    
    switch (operation) {
      case '+':
        answer = num1 + num2;
        question = `${num1} + ${num2} = ?`;
        break;
      case '-':
        answer = num1 - num2;
        question = `${num1} - ${num2} = ?`;
        break;
      case '*':
        answer = num1 * num2;
        question = `${num1} × ${num2} = ?`;
        break;
      default:
        answer = num1 + num2;
        question = `${num1} + ${num2} = ?`;
    }
    
    setCaptchaQuestion(question);
    setCaptchaAnswer(answer.toString());
    setUserCaptchaInput('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const isValidType = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type);
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      return isValidType && isValidSize;
    });
    
    if (validFiles.length !== files.length) {
      setError('Some files were rejected. Only images (JPG, PNG, GIF), PDF, and Word documents are allowed, with max size 10MB each.');
    }
    
    if (attachments.length + validFiles.length > 4) {
      setError('Maximum 4 attachments allowed.');
      return;
    }
    
    setAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatText = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      // Determine if we're fetching "My Tickets" or "All Tickets"
      const isMyTicketsTab = isAuthenticated && currentTab === getTabIndex('My Tickets');
      
      const params: any = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      
      // Filter by user email only for "My Tickets" tab
      if (isMyTicketsTab) {
        params.user_email = user?.email;
      }
      
      // Add status filter if selected
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      const response = await ticketsApi.getTickets(params);
      setTickets(response.tickets || []);
    } catch (err: any) {
      console.error('Failed to fetch tickets:', err);
      setError('Failed to load tickets.');
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch tickets for both "My Tickets" and "All Tickets" tabs
    const myTicketsIndex = isAuthenticated ? getTabIndex('My Tickets') : -1;
    const allTicketsIndex = getTabIndex('All Tickets');
    
    if (currentTab === myTicketsIndex || currentTab === allTicketsIndex) {
      fetchTickets();
    }
  }, [currentTab, page, rowsPerPage, statusFilter, isAuthenticated]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    // Reset page when switching to ticket tabs
    if (newValue !== 0) {
      setPage(0);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'error';
      case 'in_progress':
        return 'warning';
      case 'closed':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const hasTicketChanges = () => {
    if (!selectedTicket) return false;
    
    const currentSubject = editForm.subject.trim();
    const currentDescription = editEditorRef.current?.innerText?.trim() || '';
    const originalSubject = selectedTicket.subject.trim();
    const originalDescription = selectedTicket.description.trim();
    
    return currentSubject !== originalSubject || currentDescription !== originalDescription;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Get content from contentEditable div
    const description = submitEditorRef.current?.innerText || '';
    
    // Validate form
    if (!formData.user_email || !formData.user_name || !formData.subject || !description) {
      setError('Please fill in all required fields.');
      return;
    }
    
    // Validate email format
    if (!isValidEmail(formData.user_email)) {
      setError('Please enter a valid email address.');
      return;
    }
    
    // Validate CAPTCHA for non-authenticated users
    if (!isAuthenticated && userCaptchaInput !== captchaAnswer) {
      setError('Please answer the security question correctly.');
      return;
    }
    
    try {
      const ticketData = {
        ...formData,
        description: description,
      };
      
      const response = await ticketsApi.createTicket(ticketData, submitAttachments);
      
      // Reset form
      setFormData({
        user_email: user?.email || '',
        user_name: user?.full_name || user?.email || '',
        subject: '',
        description: '',
        is_logged_in_user: !!isAuthenticated,
      });
      setSubmitAttachments([]);
      
      // Clear editor
      if (submitEditorRef.current) {
        submitEditorRef.current.innerText = '';
      }
      
      setSubmittedTicket(response.ticket || null);
      setSuccess('Ticket submitted successfully!');
      handleSubmitDialogClose();
      
      // Refresh tickets list
      fetchTickets();
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment before submitting another ticket.');
      } else {
        setError(err.response?.data?.detail || 'Failed to submit ticket. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setSubmittedTicket(null);
    setSuccess('');
  };

  const handleOpenUpdateDialog = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setUpdateForm({
      status: ticket.status,
      response_notes: ticket.response_notes || '',
    });
    setUpdateDialogOpen(true);
    setError('');
  };

  const handleCloseUpdateDialog = () => {
    setUpdateDialogOpen(false);
    setSelectedTicket(null);
    setUpdateForm({
      status: 'open',
      response_notes: '',
    });
    setError('');
  };

  const handleUpdateTicket = async () => {
    if (!selectedTicket) return;

    setUpdateLoading(true);
    setError('');

    try {
      const updateData = {
        ...updateForm,
        response_by: user?.full_name || user?.email || 'Admin',
      };

      const updatedTicket = await ticketsApi.updateTicket(selectedTicket.id, updateData);
      
      // Refresh tickets list
      fetchTickets();

      setSuccess('Ticket updated successfully!');
      handleCloseUpdateDialog();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update ticket. Please try again.');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleOpenEditDialog = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setEditForm({
      subject: ticket.subject,
      description: ticket.description,
    });
    setEditAttachments([]);
    setError('');
    setEditDialogOpen(true);
    
    // Set the editor content after dialog opens
    setTimeout(() => {
      if (editEditorRef.current) {
        editEditorRef.current.innerText = ticket.description;
      }
    }, 100);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedTicket(null);
    setEditForm({
      subject: '',
      description: '',
    });
    setEditAttachments([]);
    setError('');
  };

  const handleEditTicket = async () => {
    if (!selectedTicket) return;

    setEditLoading(true);
    setError('');

    try {
      // Get content from contentEditable div
      const description = editEditorRef.current?.innerText || '';
      
      const updateData = {
        subject: editForm.subject,
        description: description,
      };

      const updatedTicket = await ticketsApi.updateTicket(selectedTicket.id, updateData);
      
      // Refresh tickets list
      fetchTickets();

      setSuccess('Ticket edited successfully!');
      handleCloseEditDialog();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to edit ticket. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Support Ticket System
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="User Guide">
              <Button 
                onClick={() => setGuideDialogOpen(true)}
                startIcon={<Help />}
                variant="outlined"
                aria-label="Help"
              >
                Guide
              </Button>
            </Tooltip>
            <Button 
              onClick={() => navigate('/')} 
              startIcon={<ArrowBack />}
              variant="outlined"
              aria-label="Back to home"
            >
              Back
            </Button>
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          Submit a support ticket and our team will assist you with your inquiry.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Submit Ticket" />
          {isAuthenticated && <Tab label="My Tickets" />}
          {isAuthenticated && <Tab label="All Tickets" />}
        </Tabs>

        {currentTab === getTabIndex('Submit Ticket') && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" gutterBottom>
              Ready to submit a support ticket?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Click the button below to open the ticket submission form
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={handleSubmitDialogOpen}
            >
              Create New Ticket
            </Button>
          </Box>
        )}

        {currentTab === getTabIndex('My Tickets') && isAuthenticated && (
          <Box>
            <Typography variant="h6" gutterBottom>
              My Submitted Tickets
            </Typography>
            
            {ticketsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : tickets.length === 0 ? (
              <Alert severity="info">
                You haven't submitted any tickets yet.
              </Alert>
            ) : (
              <Paper>
                <TableContainer
                  sx={{
                    maxHeight: '60vh',
                    overflowY: 'auto',
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ticket Number</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created Date</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                  <TableBody>
                    {tickets.map((ticket) => (
                      <TableRow key={ticket.id}>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => {
                              console.log('View ticket:', ticket);
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            {ticket.ticket_number}
                          </Link>
                        </TableCell>
                        <TableCell>{ticket.subject}</TableCell>
                        <TableCell>
                          {ticket.description.length > 100 
                            ? `${ticket.description.substring(0, 100)}...` 
                            : ticket.description}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ticket.status.replace('_', ' ')}
                            color={getStatusColor(ticket.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(ticket.created_on)}</TableCell>
                        <TableCell>{formatDate(ticket.updated_on || ticket.created_on)}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            {ticket.status === 'open' && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => handleOpenEditDialog(ticket)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  onClick={() => handleOpenUpdateDialog(ticket)}
                                >
                                  Response
                                </Button>
                              </>
                            )}
                            {ticket.status === 'in_progress' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => handleOpenUpdateDialog(ticket)}
                              >
                                Response
                              </Button>
                            )}
                            {ticket.status === 'closed' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                onClick={() => handleOpenViewDialog(ticket)}
                              >
                                View
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={tickets.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </Paper>
            )}
          </Box>
        )}

        {currentTab === getTabIndex('All Tickets') && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                All Support Tickets
              </Typography>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {ticketsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : tickets.length === 0 ? (
              <Alert severity="info">
                No tickets have been submitted yet.
              </Alert>
            ) : (
              <Paper>
                <TableContainer
                  sx={{
                    maxHeight: '60vh',
                    overflowY: 'auto',
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ticket Number</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created Date</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                  <TableBody>
                    {tickets.map((ticket) => (
                      <TableRow key={ticket.id}>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => {
                              // TODO: Navigate to ticket details
                              console.log('View ticket:', ticket);
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            {ticket.ticket_number}
                          </Link>
                        </TableCell>
                        <TableCell>{ticket.subject}</TableCell>
                        <TableCell>
                          {ticket.description.length > 100 
                            ? `${ticket.description.substring(0, 100)}...` 
                            : ticket.description}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{ticket.user_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ticket.user_email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ticket.status.replace('_', ' ')}
                            color={getStatusColor(ticket.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(ticket.created_on)}</TableCell>
                        <TableCell>{formatDate(ticket.updated_on || ticket.created_on)}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            {ticket.status === 'open' && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => handleOpenEditDialog(ticket)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  onClick={() => handleOpenUpdateDialog(ticket)}
                                >
                                  Response
                                </Button>
                              </>
                            )}
                            {ticket.status === 'in_progress' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => handleOpenUpdateDialog(ticket)}
                              >
                                Response
                              </Button>
                            )}
                            {ticket.status === 'closed' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                onClick={() => handleOpenViewDialog(ticket)}
                              >
                                View
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={tickets.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </Paper>
            )}
          </Box>
        )}

      {/* Success Dialog */}
      <Dialog open={!!submittedTicket} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" />
            Ticket Submitted Successfully
          </Box>
        </DialogTitle>
        <DialogContent>
          {submittedTicket && (
            <Box>
              <Typography variant="body1" paragraph>
                Your ticket has been submitted successfully. Our support team will review your inquiry and respond as soon as possible.
              </Typography>
              
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Ticket Details:
                </Typography>
                <Typography variant="body2">
                  <strong>Ticket Number:</strong> {submittedTicket.ticket_number}
                </Typography>
                <Typography variant="body2">
                  <strong>Subject:</strong> {submittedTicket.subject}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> 
                  <Chip 
                    label={submittedTicket.status.replace('_', ' ').toUpperCase()} 
                    size="small" 
                    color="primary" 
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {isAuthenticated && (
            <Button 
              variant="contained" 
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </Button>
            )}
        </DialogActions>
      </Dialog>

      {/* Response Ticket Dialog */}
      <Dialog open={updateDialogOpen} onClose={handleCloseUpdateDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Add Response & Update Status</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Box>
              {/* Ticket Info */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Ticket Number
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedTicket.ticket_number}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Subject
                </Typography>
                <Typography variant="body1">
                  {selectedTicket.subject}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  From
                </Typography>
                <Typography variant="body1">
                  {selectedTicket.user_name} ({selectedTicket.user_email})
                </Typography>
              </Box>

              {/* Response Form */}
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={updateForm.status}
                    label="Status"
                    onChange={(e) => setUpdateForm(prev => ({
                      ...prev,
                      status: e.target.value as 'open' | 'in_progress' | 'closed'
                    }))}
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Response Notes"
                  multiline
                  rows={4}
                  value={updateForm.response_notes}
                  onChange={(e) => setUpdateForm(prev => ({
                    ...prev,
                    response_notes: e.target.value
                  }))}
                  placeholder="Add your response or notes about this ticket update..."
                />
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog} disabled={updateLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateTicket}
            variant="contained"
            disabled={updateLoading}
            startIcon={updateLoading ? <CircularProgress size={20} /> : null}
          >
            {updateLoading ? 'Updating...' : 'Response Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Ticket Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Edit Ticket Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Box>
              {/* Ticket Info */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Ticket Number
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedTicket.ticket_number}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  From
                </Typography>
                <Typography variant="body1">
                  {selectedTicket.user_name} ({selectedTicket.user_email})
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Status
                </Typography>
                <Chip
                  label={selectedTicket.status.replace('_', ' ')}
                  color={getStatusColor(selectedTicket.status) as any}
                  size="small"
                />
              </Box>

              {/* Subject */}
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={editForm.subject}
                onChange={handleEditInputChange}
                required
                sx={{ mb: 2 }}
                placeholder="Brief description of your issue"
              />

              {/* Description with WYSIWYG Editor */}
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
                Description *
              </Typography>
              
              {/* Simple WYSIWYG Toolbar */}
              <Toolbar
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderBottom: 'none',
                  borderRadius: '4px 4px 0 0',
                  bgcolor: 'grey.50',
                }}
              >
                <Tooltip title="Bold">
                  <IconButton size="small" onClick={() => handleEditFormatText('bold')}>
                    <FormatBold />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Italic">
                  <IconButton size="small" onClick={() => handleEditFormatText('italic')}>
                    <FormatItalic />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Underline">
                  <IconButton size="small" onClick={() => handleEditFormatText('underline')}>
                    <FormatUnderlined />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Bullet List">
                  <IconButton size="small" onClick={() => handleEditFormatText('insertUnorderedList')}>
                    <FormatListBulleted />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Numbered List">
                  <IconButton size="small" onClick={() => handleEditFormatText('insertOrderedList')}>
                    <FormatListNumbered />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Align Left">
                  <IconButton size="small" onClick={() => handleEditFormatText('justifyLeft')}>
                    <FormatAlignLeft />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Align Center">
                  <IconButton size="small" onClick={() => handleEditFormatText('justifyCenter')}>
                    <FormatAlignCenter />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Align Right">
                  <IconButton size="small" onClick={() => handleEditFormatText('justifyRight')}>
                    <FormatAlignRight />
                  </IconButton>
                </Tooltip>
              </Toolbar>
              
              <Box
                ref={editEditorRef}
                contentEditable
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderTop: 'none',
                  borderRadius: '0 0 4px 4px',
                  minHeight: 200,
                  padding: 2,
                  '&:focus': {
                    outline: 'none',
                    borderColor: 'primary.main',
                  },
                  '& div': {
                    margin: 0,
                  },
                  '& p': {
                    margin: '8px 0',
                  },
                  '& p:first-child': {
                    marginTop: 0,
                  },
                  '& p:last-child': {
                    marginBottom: 0,
                  },
                }}
                style={{ minHeight: '200px' }}
              />

              {/* Attachments */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Attachments (Optional)
                </Typography>
                
                <input
                  type="file"
                  id="edit-attachments"
                  multiple
                  onChange={handleEditAttachmentChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="edit-attachments">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                    sx={{ mb: 2 }}
                  >
                    Choose Files
                  </Button>
                </label>

                {editAttachments.length > 0 && (
                  <List>
                    {editAttachments.map((file, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleEditRemoveAttachment(index)}
                          >
                            <Delete />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={file.name}
                          secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={editLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditTicket}
            variant="contained"
            disabled={editLoading || !hasTicketChanges()}
            startIcon={editLoading ? <CircularProgress size={20} /> : null}
          >
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit Ticket Dialog */}
      <Dialog open={submitDialogOpen} onClose={handleSubmitDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Submit New Ticket</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit}>
            {/* User Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Contact Information
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="user_email"
                  type="email"
                  value={formData.user_email}
                  onChange={handleInputChange}
                  required
                  disabled={isAuthenticated}
                  helperText={
                    isAuthenticated 
                      ? "Auto-filled from your account" 
                      : formData.user_email && !isValidEmail(formData.user_email)
                        ? "Please enter a valid email address"
                        : "Enter your email address"
                  }
                  error={formData.user_email.length > 0 && !isValidEmail(formData.user_email)}
                  size="small"
                />
                
                <TextField
                  fullWidth
                  label="Full Name"
                  name="user_name"
                  value={formData.user_name}
                  onChange={handleInputChange}
                  required
                  disabled={isAuthenticated}
                  helperText={isAuthenticated ? "Auto-filled from your account" : ""}
                  size="small"
                />
              </Box>
            </Box>

            {/* Subject */}
            <TextField
              fullWidth
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
              placeholder="Brief description of your issue"
              size="small"
            />

            {/* Description with WYSIWYG Editor */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
              Description *
            </Typography>
            
            {/* Simple WYSIWYG Toolbar */}
            <Toolbar
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderBottom: 'none',
                borderRadius: '4px 4px 0 0',
                bgcolor: 'grey.50',
                minHeight: 'auto',
              }}
              variant="dense"
            >
              <Tooltip title="Bold">
                <IconButton size="small" onClick={() => handleFormatSubmitText('bold')}>
                  <FormatBold fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Italic">
                <IconButton size="small" onClick={() => handleFormatSubmitText('italic')}>
                  <FormatItalic fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Underline">
                <IconButton size="small" onClick={() => handleFormatSubmitText('underline')}>
                  <FormatUnderlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Bullet List">
                <IconButton size="small" onClick={() => handleFormatSubmitText('insertUnorderedList')}>
                  <FormatListBulleted fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Numbered List">
                <IconButton size="small" onClick={() => handleFormatSubmitText('insertOrderedList')}>
                  <FormatListNumbered fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Align Left">
                <IconButton size="small" onClick={() => handleFormatSubmitText('justifyLeft')}>
                  <FormatAlignLeft fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Align Center">
                <IconButton size="small" onClick={() => handleFormatSubmitText('justifyCenter')}>
                  <FormatAlignCenter fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Align Right">
                <IconButton size="small" onClick={() => handleFormatSubmitText('justifyRight')}>
                  <FormatAlignRight fontSize="small" />
                </IconButton>
              </Tooltip>
            </Toolbar>
            
            <Box
              ref={submitEditorRef}
              contentEditable
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderTop: 'none',
                borderRadius: '0 0 4px 4px',
                minHeight: 150,
                maxHeight: 300,
                overflowY: 'auto',
                padding: 2,
                '&:focus': {
                  outline: 'none',
                  borderColor: 'primary.main',
                },
                '& div': {
                  margin: 0,
                },
                '& p': {
                  margin: '4px 0',
                },
                '& p:first-child': {
                  marginTop: 0,
                },
                '& p:last-child': {
                  marginBottom: 0,
                },
              }}
            />

            {/* Attachments */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Attachments (Optional)
              </Typography>
              
              <input
                type="file"
                id="submit-attachments"
                multiple
                onChange={handleSubmitAttachmentChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="submit-attachments">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  Choose Files
                </Button>
              </label>

              {submitAttachments.length > 0 && (
                <List dense>
                  {submitAttachments.map((file, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleSubmitRemoveAttachment(index)}
                          size="small"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      }
                    >
                      <AttachFile fontSize="small" sx={{ mr: 1 }} />
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {/* CAPTCHA for non-authenticated users */}
            {!isAuthenticated && (
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Security Verification
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1,
                  border: '1px solid rgba(0, 0, 0, 0.23)'
                }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: '120px' }}>
                    {captchaQuestion}
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Your answer"
                    value={userCaptchaInput}
                    onChange={(e) => setUserCaptchaInput(e.target.value)}
                    sx={{ minWidth: '120px' }}
                  />
                  <IconButton 
                    size="small" 
                    onClick={generateCaptcha}
                    title="Refresh question"
                  >
                    <Refresh />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Please solve this simple math problem to prove you're human.
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmitDialogClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.user_email.trim() || !formData.user_name.trim() || !formData.subject.trim() || !submitEditorRef.current?.innerText?.trim() || !isValidEmail(formData.user_email) || (!isAuthenticated && !userCaptchaInput.trim())}
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          >
            {loading ? 'Submitting...' : 'Submit Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Ticket Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Ticket Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewTicket && (
            <Box>
              {/* Ticket Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Ticket Information
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Ticket Number
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {viewTicket.ticket_number}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Created Date
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(viewTicket.created_on)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(viewTicket.updated_on || viewTicket.created_on)}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={viewTicket.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      color={getStatusColor(viewTicket.status) as any}
                      size="small"
                      sx={{ mt: 0.5, ml:2 }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* User Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Contact Information
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body2">
                      {viewTicket.user_name}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body2">
                      {viewTicket.user_email}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Subject */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Subject
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {viewTicket.subject}
                </Typography>
              </Box>

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Box 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1,
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    minHeight: 100,
                    '& p': { margin: '4px 0' },
                    '& p:first-child': { marginTop: 0 },
                    '& p:last-child': { marginBottom: 0 }
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: viewTicket.description.replace(/\n/g, '<br>') 
                  }}
                />
              </Box>

              {/* Response Information (if available) */}
              {viewTicket.response_notes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Response
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Response By
                      </Typography>
                      <Typography variant="body2">
                        {viewTicket.response_by}
                      </Typography>
                    </Box>
                  </Box>

                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'success.light', 
                      borderRadius: 1,
                      border: '1px solid rgba(76, 175, 80, 0.3)',
                      '& p': { margin: '4px 0' },
                      '& p:first-child': { marginTop: 0 },
                      '& p:last-child': { marginBottom: 0 }
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: viewTicket.response_notes.replace(/\n/g, '<br>') 
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Guide Dialog */}
      <TicketingUserGuide 
        open={guideDialogOpen} 
        onClose={() => setGuideDialogOpen(false)} 
      />
    </Box>
  );
};

export default TicketingPage;
